import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { SinequaService } from './sinequa.service';
import { SinequaSearchResponse } from '../models/sinequa.model';

/**
 * Decides where a fresh text query lands.
 *
 * A query the catalog cannot answer used to end on an empty results page. It
 * now goes straight to the assistant, which can answer in prose what the index
 * has no record for. Anything with at least one hit still goes to the results
 * page, where the assistant widget comments on it.
 *
 * The check runs *before* navigating, so the user never sees the results page
 * flash on the way to the chat. The cost is one extra Sinequa call per hit —
 * a deliberate trade, kept cheap by asking for a single row.
 *
 * Only fresh text queries route through here. Narrowing by filter or paging to
 * an empty page keeps the user on the results page: they are already browsing,
 * and throwing them into a chat because they ticked one box too many would be
 * hostile.
 *
 * An empty submit is a third case: nothing to probe, so it opens the chat
 * directly and lets the user start there.
 */
@Injectable({
  providedIn: 'root',
})
export class SearchNavigationService {
  private readonly _router = inject(Router);
  private readonly _sinequa = inject(SinequaService);

  private readonly _resolving = signal(false);

  /** True while the probe is in flight, so a field can show it is busy. */
  readonly isResolving = this._resolving.asReadonly();

  /** Run `query` and navigate to whichever page can actually answer it. */
  submitQuery(query: string): void {
    const q = query.trim();
    if (this._resolving()) {
      return;
    }

    // An empty box asks for the assistant itself rather than for a search, so
    // it skips the probe — there is nothing to look up — and goes straight to
    // the chat, which focuses its composer on arrival. This used to return
    // silently, which left "Ask Panorama" looking broken to exactly the people
    // who had not yet worked out what to type.
    //
    // Note this sits *after* the re-entrancy guard, so a blank submit landing
    // while a probe is in flight is dropped rather than racing it: that probe is
    // about to navigate on its own, and two navigations would fight.
    if (!q) {
      this._router.navigate(['/assistant']);
      return;
    }

    this._resolving.set(true);

    // `trackLoading: false` — this is a probe, not the page's own search, and
    // flipping SinequaService's shared loading signal would put the results
    // page into a busy state it never asked for.
    this._sinequa
      .search({ query: q, page: 1, pageSize: 1 }, false)
      .subscribe({
        // Annotated because `SinequaService.search()` is still typed
        // `Observable<any>`; this is where that `any` stops.
        next: (response: SinequaSearchResponse) => {
          this._resolving.set(false);
          this._go(q, this._hasResults(response));
        },
        // Belt and braces: SinequaService already catches and returns an error
        // object, so this only fires if that ever stops being true.
        error: () => {
          this._resolving.set(false);
          this._go(q, true);
        },
      });
  }

  /**
   * Whether the catalog has anything for this query.
   *
   * A failed request is NOT "no results". SinequaService catches HTTP errors
   * and resolves with `{ error: true, totalCount: 0, … }` — note `totalCount`,
   * not the `totalRowCount` a real response carries — so without the explicit
   * error check every outage would read as an empty catalog and send people to
   * the assistant. On failure we assume results and let the results page report
   * the error properly.
   */
  private _hasResults(response: SinequaSearchResponse | null): boolean {
    if (!response || response.error) {
      return true;
    }
    return (response.totalRowCount ?? 0) > 0;
  }

  private _go(q: string, hasResults: boolean): void {
    const path = hasResults ? '/search' : '/assistant';
    this._router.navigate([path], { queryParams: { q } });
  }
}
